import React from 'react'

// Constants
import countries from 'constants/countries'

// Utils
import {
  confirmPendingSetupIntent,
  isPaymentMethodCard,
  isPaymentMethodInvoice,
  isWorkspaceSubscriptionActive,
} from 'modules/workspace/utils'
import { ErrorHandler } from 'modules/tracing'

// Components
import Banner from 'components/Banner'
import Panel from '../Panel'
import PaymentLogo from 'components/PaymentLogo'
import RowGroup, { panelActionCellProps } from '../RowGroup'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { useStripe } from 'containers/StripeProvider'

// Local Components
import EditAddressDetailsModal from 'modules/workspace/modals/EditAddressDetailsModal'
import EditPaymentMethodModal from 'modules/workspace/modals/EditPaymentMethodModal'
import { VatStatus } from 'modules/workspace/components/VatStatus'

// Styles
import {
  PaymentInformationWrapper,
  List,
  StyledButton,
  StyledAsyncButton,
  PaymentLogoWrapper,
  SCAWarningPill,
  CreditBalanceTextContainer,
  CreditLogoAmountContainer,
} from './PaymentInformationPanel.styles'

// Types
import { BillingDetailsFragment, PaymentDetailsFragment } from 'gqlTypes'

import { formatPriceShort } from 'utils/money'

// This is a valid use case of expansive types
// eslint-disable-next-line no-restricted-imports
import { BillingStatus } from 'gqlTypes/expansiveTypes'

const IGNORE_UI_REFRESH_STATUS = ['ACTIVE', 'TRIAL']

type Countries = keyof typeof countries

interface PaymentInformationPanelProps {
  customerId: string
  credits: number
  billingDetails: BillingDetailsFragment
  paymentDetails?: PaymentDetailsFragment
  subscriptionStatus: BillingStatus
  pendingSCAToken?: string
  onWorkspaceSettingRefresh: () => Promise<void>
}

/*
 * PaymentInformationPanel
 *
 * Renders the Payments Information panel inside the Billing page
 */
export const PaymentInformationPanel: React.FC<PaymentInformationPanelProps> = props => {
  const {
    customerId,
    billingDetails,
    paymentDetails,
    credits,
    subscriptionStatus,
    pendingSCAToken,
    onWorkspaceSettingRefresh,
  } = props
  const { showModal } = useModalContext()
  const { stripe } = useStripe()

  // Billing details
  const { name, email, address, taxId } = billingDetails

  const billingItems: (string | null)[] = [name, email]

  if (address) {
    const stateAndPostalCode = [address.state, address.postalCode].join(' ')
    const country = countries[address.country as Countries] || address.country

    billingItems.push(
      null, // A blank line separating email from address
      address?.line1,
      address?.city,
      stateAndPostalCode,
      country
    )
  }

  const onPaymentUpdated = async () => {
    /**
     * We need to refetch most of the workspace settings information
     * because when updating payment method you can now reactivate
     * a old workspace with a canceled subscription or validate a card
     * that had a pending 3D secure challenge.
     *
     * With these interactions the data returned by the API might be
     * different in a lot of parameters like the number of seats or the
     * plan renewal date in order, and instead of manually updating the
     * cache with this values we opted by refetching the queries again
     * preventing error prone information
     */

    if (IGNORE_UI_REFRESH_STATUS.includes(subscriptionStatus)) {
      /**
       * We can safely ignore these status because most of the workspaces
       * information won't change
       */
      return
    }

    await onWorkspaceSettingRefresh()
  }

  // Prepare the UI for each Payment method type
  let paymentContent = null

  if (
    paymentDetails &&
    isPaymentMethodCard(paymentDetails) &&
    pendingSCAToken
  ) {
    const { last4Digits, brand, expiration } = paymentDetails
    paymentContent = (
      <>
        <RowGroup.CaptionCell width="70%">
          <PaymentLogoWrapper>
            <PaymentLogo brand={brand} />
            &nbsp; ···· {last4Digits} · Expires {expiration.month}/
            {String(expiration.year).slice(-2)}
          </PaymentLogoWrapper>
          <SCAWarningPill color="orange" variant={{ kind: 'primary' }}>
            3D Secure Authentication Needed
          </SCAWarningPill>
        </RowGroup.CaptionCell>
        <RowGroup.Cell {...panelActionCellProps}>
          <StyledAsyncButton
            small
            data-test="sca-verify-button"
            onClick={async () => {
              if (!stripe) {
                return
              }

              await confirmPendingSetupIntent(stripe, pendingSCAToken)
              await onPaymentUpdated()
            }}>
            Verify&hellip;
          </StyledAsyncButton>
          <StyledButton
            small
            data-test="credit-card-button"
            onClick={() =>
              showModal(EditPaymentMethodModal, {
                customerId,
                onPaymentUpdated,
              })
            }>
            Edit&hellip;
          </StyledButton>
        </RowGroup.Cell>
      </>
    )
  } else if (paymentDetails && isPaymentMethodCard(paymentDetails)) {
    const { last4Digits, brand, expiration } = paymentDetails
    paymentContent = (
      <>
        <RowGroup.CaptionCell width="70%">
          <PaymentLogoWrapper>
            <PaymentLogo brand={brand} />
            &nbsp; ···· {last4Digits} · Expires {expiration.month}/
            {String(expiration.year).slice(-2)}
          </PaymentLogoWrapper>
        </RowGroup.CaptionCell>
        <RowGroup.Cell {...panelActionCellProps}>
          <StyledButton
            tiny
            data-test="credit-card-button"
            onClick={() =>
              showModal(EditPaymentMethodModal, {
                customerId,
                onPaymentUpdated,
              })
            }>
            Edit&hellip;
          </StyledButton>
        </RowGroup.Cell>
      </>
    )
  } else if (paymentDetails && isPaymentMethodInvoice(paymentDetails)) {
    paymentContent = (
      <RowGroup.CaptionCell width="70%">
        <PaymentLogoWrapper>
          <PaymentLogo brand="invoice" /> &nbsp;&nbsp;Invoice Payment
        </PaymentLogoWrapper>
      </RowGroup.CaptionCell>
    )
  }

  return (
    <PaymentInformationWrapper>
      <Panel.Title>Payment information</Panel.Title>
      <Banner type="information">
        All transactions are securely encrypted by{' '}
        <a target="_blank" href="https://stripe.com" rel="noopener noreferrer">
          Stripe
        </a>
      </Banner>
      <br />
      <Panel.Body>
        <RowGroup>
          {credits !== 0 && (
            <RowGroup.Row>
              <RowGroup.HeadingCell width={['70%', 1 / 4]}>
                Credit Balance
              </RowGroup.HeadingCell>
              <RowGroup.CaptionCell width="70%">
                <CreditBalanceTextContainer>
                  <CreditLogoAmountContainer>
                    <PaymentLogo brand="credit" />
                    {formatPriceShort(credits)}
                  </CreditLogoAmountContainer>
                  <div>
                    We’ll always use your available credit balance before
                    charging your card.
                  </div>
                </CreditBalanceTextContainer>
              </RowGroup.CaptionCell>
            </RowGroup.Row>
          )}
          {paymentContent && (
            <RowGroup.Row>
              <RowGroup.HeadingCell width={['70%', 1 / 4]}>
                Payment Method
              </RowGroup.HeadingCell>
              {paymentContent}
            </RowGroup.Row>
          )}
          {paymentDetails && paymentDetails.type !== 'NONE' && (
            <RowGroup.Row>
              <RowGroup.HeadingCell width={['70%', 1 / 4]}>
                Billing Details
              </RowGroup.HeadingCell>
              <RowGroup.CaptionCell width="70%">
                <List>
                  {billingItems.map((item, index) => (
                    <li key={index}>{item || <>&nbsp;</>}</li>
                  ))}
                  {taxId && <VatStatus taxId={taxId} />}
                </List>
              </RowGroup.CaptionCell>
              {isWorkspaceSubscriptionActive(subscriptionStatus) &&
                paymentDetails &&
                isPaymentMethodCard(paymentDetails) && (
                  <RowGroup.Cell
                    justifyContent={['flex-start', 'flex-start', 'flex-end']}
                    alignItems="flex-start">
                    <StyledButton
                      tiny
                      data-test="billing-address-button"
                      onClick={() => {
                        if (billingDetails.address) {
                          const { address } = billingDetails

                          showModal(EditAddressDetailsModal, {
                            customerId,
                            // Force the type to be checked correctly
                            billingDetails: {
                              ...billingDetails,
                              address,
                            },
                          })
                        } else {
                          ErrorHandler.shouldNeverHappen(
                            'A Workspace with a payment method should always have a Billing address'
                          )
                        }
                      }}>
                      Edit&hellip;
                    </StyledButton>
                  </RowGroup.Cell>
                )}
            </RowGroup.Row>
          )}
        </RowGroup>
      </Panel.Body>
    </PaymentInformationWrapper>
  )
}
